<template>
  <div class="product-family-compatibilities-view" v-if="compatibilities">
    <table
      class="uk-table uk-table-divider uk-margin-remove"
      v-if="compatibilities.length"
    >
      <thead>
        <tr>
          <th>{{$t('fields.name')}}</th>
          <th>{{$t('fields.familyNumber')}}</th>
          <th colspan="2">{{$t('fields.requirements')}}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          :key="compatibility.id"
          class="product-compatibility-manager__row"
          v-for="compatibility in compatibilities"
        >
          <td>
            <router-link
              :to="{
                name: 'product-family',
                params: { id: compatibility.compatibleFamily.id }
              }"
            >{{compatibility.compatibleFamily.name}}</router-link>
          </td>
          <td>
            <text-placeholder
              :text="compatibility.compatibleFamily.familyNumber"
            />
          </td>
          <td>
            <product-family-compatibility-requirements-count
              :compatibility="compatibility"
            />
          </td>
          <td>
            <data-grid-actions
              @edit="openCompatibilityForm(compatibility)"
              @remove="deleteCompatibilityWhenConfirmed(compatibility)"
            />
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td
            class="uk-padding-remove-left uk-padding-remove-right"
            colspan="4"
          >
            <common-button
              @click="openCompatibilityForm()"
              icon="plus"
              type="primary"
            >{{$t('actions.add')}}</common-button>
          </td>
        </tr>
      </tfoot>
    </table>

    <div class="uk-placeholder uk-text-center uk-margin-remove" v-else>
      <div class="uk-margin-bottom">{{$t('hints.empty')}}</div>
      <common-button
        @click="$refs.compatibilityFormDialog.show()"
        icon="plus"
        type="primary"
      >{{$t('actions.add')}}</common-button>
    </div>

    <product-family-compatibility-form
      :disabled="isSubmitting"
      :errors="errors"
      :unselectable-family-ids="unselectableFamilyIds"
      :values="editedCompatibility"
      @hidden="onCompatibilityFormHidden"
      @submit="upsertCompatibility"
      ref="compatibilityFormDialog"
    />
  </div>

  <div class="uk-margin-medium-top uk-text-center" v-else>
    <loader />
  </div>
</template>

<script>
import UIkit from 'uikit';
import View from '@/mixins/View';
import Authentication from '@/mixins/Authentication';
import CommonButton from '@/components/CommonButton';
import DataGridActions from '@/components/DataGridActions';
import deleteProductFamilyCompatibilityMutation
  from '@/api/mutations/delete-product-family-compatibility.graphql';
import Draggable from 'vuedraggable';
import insertProductFamilyCompatibilityMutation
  from '@/api/mutations/insert-product-family-compatibility.graphql';
import Loader from '@/components/Loader';
import productFamilyCompatibilitiesQuery
  from '@/api/queries/product-family-compatibilities.graphql';
import ProductFamilyCompatibilityForm
  from '@/components/ProductFamilyCompatibilityForm';
import ProductFamilyCompatibilityRequirementsCount
  from '@/components/ProductFamilyCompatibilityRequirementsCount';
import Submittable from '@/mixins/Submittable';
import TextPlaceholder from '@/components/TextPlaceholder';
import Toast from '@/helpers/Toast';
import updateProductFamilyCompatibilityMutation
  from '@/api/mutations/update-product-family-compatibility.graphql';

export default {
  mixins: [
    Authentication,
    Submittable,
    View,
  ],
  components: {
    CommonButton,
    DataGridActions,
    Draggable,
    Loader,
    ProductFamilyCompatibilityForm,
    ProductFamilyCompatibilityRequirementsCount,
    TextPlaceholder,
  },
  props: {
    productFamily: Object,
  },
  data() {
    return {
      editedCompatibility: null,
    };
  },
  computed: {
    breadcrumbs() {
      return [
        {
          key: 'show',
          text: () => this.$t('title'),
          route: { name: 'product-family-compatibilities' },
          isLoading: false,
        },
      ];
    },
    tableLoaded() {
      return this.tableInitiallyLoaded && this.compatibilities;
    },
    unselectableFamilyIds() {
      const ids = [this.productFamily.id];
      if (!this.compatibilities) return ids;
      return ids.concat(this.compatibilities.map(r => r.compatibleFamily.id));
    },
  },
  methods: {
    async openCompatibilityForm(compatibility = null) {
      this.editedCompatibility = compatibility;
      await this.$nextTick();
      this.$refs.compatibilityFormDialog.show();
    },
    async upsertCompatibility(formData) {
      let mutation;
      let toastKey;
      let variables;

      if (this.editedCompatibility) {
        mutation = updateProductFamilyCompatibilityMutation;
        variables = this.updateCompatibilityVarsFromFormData(formData);
        toastKey = 'updated';
      } else {
        mutation = insertProductFamilyCompatibilityMutation;
        variables = this.insertCompatibilityVarsFromFormData(formData);
        toastKey = 'created';
      }

      const operation = await this.submitWithMutation({
        mutation,
        variables,
        refetchQueries: [
          {
            query: productFamilyCompatibilitiesQuery,
            variables: { filter: { familyIds: [this.productFamily.id] } },
          },
        ],
      });

      if (operation.isValid) {
        const compatibleFamily = operation.compatibility.compatibleFamily;

        const message =
          this.$t(`toasts.${toastKey}`, { name: compatibleFamily.name });

        this.$refs.compatibilityFormDialog.hide();
        Toast.success(message);
      }
    },
    insertCompatibilityVarsFromFormData(formData) {
      return {
        compatibility: {
          familyId: this.productFamily.id,
          compatibleFamilyId: formData.compatibleFamilyId,
          requirementAlternatives: formData.requirementAlternatives,
        },
      };
    },
    updateCompatibilityVarsFromFormData(formData) {
      return {
        id: this.editedCompatibility.id,
        compatibility: {
          requirementAlternatives: formData.requirementAlternatives,
        },
      };
    },
    async deleteCompatibilityWhenConfirmed(compatibility) {
      const message = this.$t('confirmations.delete', {
        name: compatibility.compatibleFamily.name,
      });

      try {
        await UIkit.modal.confirm(message);
        this.deleteCompatibility(compatibility);
      } catch (error) {
        // do nothing
      }
    },
    async deleteCompatibility(compatibility) {
      await this.$apollo.mutate({
        mutation: deleteProductFamilyCompatibilityMutation,
        variables: { id: compatibility.id },
        refetchQueries: [
          {
            query: productFamilyCompatibilitiesQuery,
            variables: { filter: { familyIds: [this.productFamily.id] } },
          },
        ],
      });
      const message = this.$t('toasts.deleted', {
        name: compatibility.compatibleFamily.name,
      });
      Toast.success(message);
    },
    onCompatibilityFormHidden() {
      this.editedCompatibility = null;
    },
  },
  apollo: {
    compatibilities: {
      query: productFamilyCompatibilitiesQuery,
      variables() {
        return { filter: { familyIds: [this.productFamily.id] } };
      },
      fetchPolicy: 'network-only',
      loadingKey: 'loading',
      update(data) {
        return data.productFamilyCompatibilities.entries;
      },
      skip() {
        return !this.isAuthenticated;
      },
    },
  },
  i18n: {
    messages: {
      de: {
        title: 'Kompatibilitäten',
        confirmations: {
          delete: 'Kompatibilität wirklich löschen?',
        },
        fields: {
          requirements: 'Abhängigkeiten',
        },
        hints: {
          empty: 'Keine kompatiblen Produktfamilien gefunden.',
        },
        toasts: {
          created: 'Kompatibilität hinzugefügt.',
          updated: 'Kompatibilität aktualisiert.',
          deleted: 'Kompatibilität entfernt.',
        },
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// CompatibilitiesView.vue?25ab4e95